import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Button from "../../../../components/buttons/buttons";
import Input from "../../../../components/inputs/inputs";
import { editRevenueSettingsAction } from "../../../../redux/revenueSettings/revenueSettingsActions";

/**
 * Edits existing revenue settings for a business or shipper.
 * @param {string} ownerId - the business or shipper id
 * @param {"business" | "shipper"} type
 * @param {object} settings - the current revenue settings
 */
export default function EditRevenueSettingsForm({
  ownerId,
  type = "business",
  settings,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const backPath = `/admin/revenue/revenue-settings/${
    type === "shipper" ? "shippers" : "businesses"
  }`;

  const [data, setData] = useState({
    percentage: settings?.percentage ?? "",
    minimum_amount: settings?.minimum_amount ?? 0,
    maximum_amount: settings?.maximum_amount ?? 0,
    status: settings?.status ?? "available",
    threshold_amount: settings?.threshold_amount ?? "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const percentage = Number(data.percentage);
  const minimumAmount = Number(data.minimum_amount);
  const maximumAmount = Number(data.maximum_amount);

  // maximum_amount of 0 is treated as "no maximum"
  let error = "";
  if (
    [
      data.percentage,
      data.minimum_amount,
      data.maximum_amount,
      data.threshold_amount,
    ].includes("")
  ) {
    error = "All fields are required";
  } else if (percentage > 100) {
    error = "Percentage must be between 0 and 100";
  } else if (maximumAmount > 0 && maximumAmount < minimumAmount) {
    error = "Maximum amount must be greater than or equal to minimum amount";
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData({ ...data, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (error) return;
    setIsSubmitting(true);

    const response = await dispatch(
      editRevenueSettingsAction({
        id: settings?.id,
        [type === "shipper" ? "shipper_id" : "business_id"]: ownerId,
        type,
        percentage,
        minimum_amount: minimumAmount,
        maximum_amount: maximumAmount,
        threshold_amount: Number(data.threshold_amount),
        status: data.status,
      }),
    );
    setIsSubmitting(false);

    if (response?.status === "success") {
      navigate(backPath);
    }
  };

  return (
    <div data-test="business-split-form-page">
      <div className="mb-6">
        <Button.Secondary
          title="← Back to Revenue Settings"
          onClick={() => navigate(backPath)}
        />
      </div>

      <form
        onSubmit={handleSubmit}
        className="border border-[#E4E4F3] dark:border-none bg-white dark:bg-[#121212] rounded-md py-5 px-4 sm:px-8"
      >
        <div className="border-b border-[#E4E4F3] dark:border-[#505050] py-3 mb-6 flex justify-between items-center w-full">
          <h3 className="text-lg font-medium">Edit Revenue Settings</h3>
          <Button.Primary
            title={isSubmitting ? "Processing..." : "Update Revenue Settings"}
            type="submit"
            disabled={isSubmitting || Boolean(error)}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="mb-4">
            <Input.Number
              title="Percentage (%)*"
              name="percentage"
              value={data.percentage}
              onChange={handleChange}
              min="0"
              placeholder={"Enter percentage"}
            />
          </div>
          <div className="mb-4">
            <Input.Number
              title="Minimum Amount*"
              name="minimum_amount"
              value={data.minimum_amount}
              onChange={handleChange}
              min="0"
              placeholder={"Enter minimum amount"}
            />
          </div>
          <div className="mb-4">
            <Input.Number
              title="Maximum Amount*"
              name="maximum_amount"
              value={data.maximum_amount}
              onChange={handleChange}
              min="0"
              placeholder={"Enter maximum amount"}
              subText={"Leave as 0 for no maximum"}
            />
          </div>
          <div className="mb-4">
            <Input.Number
              title="Threshold Amount*"
              name="threshold_amount"
              value={data.threshold_amount}
              onChange={handleChange}
              min="0"
              placeholder={"Enter threshold amount"}
            />
          </div>
          <div className="mb-4">
            <Input.Select
              title={"Revenue Status*"}
              name="status"
              value={data.status}
              onChange={handleChange}
            >
              <option value={"available"}>Available</option>
              <option value={"unavailable"}>Unavailable</option>
            </Input.Select>
          </div>
        </div>

        {error && data.percentage !== "" && (
          <p className="text-sm text-[#CE2600]">{error}</p>
        )}
      </form>
    </div>
  );
}
